import { useEffect, useState, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const HANDLE_REGEX = /^[A-Za-z0-9_.]{3,30}$/;

export const SUGGESTED_HANDLES = [
  "analyst", "market_owl", "delta_hedger", "chai_trader", "signal_seeker",
  "value_hunter", "alpha_seeker", "candle_reader", "macro_mind", "quant_kid",
  "vol_crusher", "tape_reader", "edge_finder", "risk_doc", "theta_gang",
];

export function generateSuggestions(count = 5): string[] {
  const pool = [...SUGGESTED_HANDLES].sort(() => Math.random() - 0.5).slice(0, count);
  return pool.map((p) => `${p}_${Math.floor(Math.random() * 900) + 100}`);
}

export function useCommunityHandle(user: User | null) {
  const [handle, setHandle] = useState<string | null>(null);
  const [avatarId, setAvatarId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [bio, setBio] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setHandle(null);
      setAvatarId(null);
      setDisplayName(null);
      setCountryCode(null);
      setBio(null);
      setIsLoading(false);
      return;
    }
    const { data } = await supabase
      .from("community_handles")
      .select("handle, avatar_id, display_name, country_code, bio")
      .eq("user_id", user.id)
      .maybeSingle();
    setHandle(data?.handle ?? null);
    setAvatarId((data as any)?.avatar_id ?? null);
    setDisplayName((data as any)?.display_name ?? null);
    setCountryCode((data as any)?.country_code ?? null);
    setBio((data as any)?.bio ?? null);
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const checkAvailable = useCallback(async (candidate: string): Promise<boolean> => {
    if (!HANDLE_REGEX.test(candidate)) return false;
    const { data } = await supabase
      .from("community_handles")
      .select("user_id")
      .ilike("handle", candidate)
      .maybeSingle();
    return !data || data.user_id === user?.id;
  }, [user?.id]);

  const saveHandle = useCallback(async (candidate: string, newAvatarId?: string | null): Promise<{ ok: boolean; error?: string }> => {
    if (!user) return { ok: false, error: "Not signed in" };
    if (!HANDLE_REGEX.test(candidate)) return { ok: false, error: "3-30 chars, letters/numbers/underscore/dot only" };
    const payload: any = { user_id: user.id, handle: candidate };
    if (newAvatarId !== undefined) payload.avatar_id = newAvatarId;
    const { error } = await supabase
      .from("community_handles")
      .upsert(payload, { onConflict: "user_id" });
    if (error) return { ok: false, error: error.message };
    setHandle(candidate);
    if (newAvatarId !== undefined) setAvatarId(newAvatarId);
    return { ok: true };
  }, [user]);

  const saveAvatar = useCallback(async (newAvatarId: string | null): Promise<{ ok: boolean; error?: string }> => {
    if (!user) return { ok: false, error: "Not signed in" };
    if (!handle) return { ok: false, error: "Set a handle first" };
    const { error } = await supabase
      .from("community_handles")
      .update({ avatar_id: newAvatarId })
      .eq("user_id", user.id);
    if (error) return { ok: false, error: error.message };
    setAvatarId(newAvatarId);
    return { ok: true };
  }, [user, handle]);

  const saveProfile = useCallback(async (
    fields: { display_name?: string | null; country_code?: string | null; bio?: string | null },
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!user) return { ok: false, error: "Not signed in" };
    if (!handle) return { ok: false, error: "Set a handle first" };
    const payload: Record<string, unknown> = {};
    if (fields.display_name !== undefined) payload.display_name = fields.display_name;
    if (fields.country_code !== undefined) payload.country_code = fields.country_code;
    if (fields.bio !== undefined) payload.bio = fields.bio;
    const { error } = await supabase
      .from("community_handles")
      .update(payload)
      .eq("user_id", user.id);
    if (error) return { ok: false, error: error.message };
    if (fields.display_name !== undefined) setDisplayName(fields.display_name);
    if (fields.country_code !== undefined) setCountryCode(fields.country_code);
    if (fields.bio !== undefined) setBio(fields.bio);
    return { ok: true };
  }, [user, handle]);

  return {
    handle, avatarId, displayName, countryCode, bio,
    isLoading, refresh, checkAvailable, saveHandle, saveAvatar, saveProfile,
  };
}